import { Handle, Position } from '@xyflow/react'
import { isFired } from './useFiredFlash'

/**
 * Rendezvous (AND-join). Fires its exec output only once BOTH inputs A and B
 * have arrived — a barrier, not a merge. Memory is node-local and lives in the
 * runtime (window.__RENDEZVOUS_STATE__, keyed by node id); it auto-resets after
 * firing so the node re-arms for the next cycle. The optional Reset input clears
 * a half-completed join. See EventRuntime `rendezvous` case + rendezvousState.js.
 *
 * The A/B dots show live arrival state — ● arrived (waiting), ○ not yet. The
 * node re-renders on the editor's debug poll (the runtime bumps debugState.tick
 * on each arrival), so the dots update as streams come in.
 */

// Exported so EventGraph's palette entry reuses it instead of hardcoding a
// second copy of the literal (keeps the palette hex-lock from growing).
export const RENDEZVOUS_ACCENT = '#c090f0'

const HEADER = '#160a2a'
const EXEC   = '#ddd'   // exec pins are neutral squares, repo convention
const execStyle = { background: EXEC, width: 12, height: 12, borderRadius: 2, border: '2px solid #aaa' }

function ExecInRow({ id, label, arrived, showDot, borderColor }) {
  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 8,
      padding: '6px 12px 6px 16px', borderBottom: `1px solid ${borderColor}`, position: 'relative',
    }}>
      <Handle type="target" position={Position.Left} id={id} style={execStyle} />
      <span style={{ color: showDot ? RENDEZVOUS_ACCENT : '#6a5a80', fontSize: 10 }}>{label}</span>
      {showDot && (
        <span style={{ marginLeft: 'auto', color: arrived ? '#7ad29a' : '#4a3a60', fontSize: 12, lineHeight: 1 }}>
          {arrived ? '●' : '○'}
        </span>
      )}
    </div>
  )
}

export function RendezvousNode({ id, data, selected }) {
  const fired = isFired(data)
  const st = (typeof window !== 'undefined' && window.__RENDEZVOUS_STATE__?.get(id)) || null

  return (
    <div style={{
      background: selected ? '#1a0e30' : '#0e0620',
      border: `2px solid ${fired ? '#ffcc44' : selected ? RENDEZVOUS_ACCENT : '#3a2858'}`,
      borderRadius: 6, minWidth: 200, fontFamily: 'var(--m-font)', fontSize: 12,
      boxShadow: fired ? '0 0 14px rgba(255,200,40,0.6)' : selected ? `0 0 12px ${RENDEZVOUS_ACCENT}33` : 'none',
    }}>
      {/* Header + exec output */}
      <div style={{
        background: HEADER, padding: '6px 12px',
        display: 'flex', alignItems: 'center', gap: 8, position: 'relative',
        borderRadius: '4px 4px 0 0',
      }}>
        <span style={{ fontSize: 13 }}>⇉</span>
        <span style={{ color: RENDEZVOUS_ACCENT, fontWeight: 'bold', fontSize: 11, letterSpacing: 0.5 }}>Rendezvous</span>
        <Handle type="source" position={Position.Right} id="execOut" style={execStyle} />
      </div>

      <div style={{ padding: '6px 12px', color: '#6a5a80', fontSize: 10, borderBottom: '1px solid #140a24' }}>
        Fires only when BOTH A and B have arrived. Auto-resets after.
      </div>

      <ExecInRow id="execInA"   label="A"     arrived={!!st?.a} showDot borderColor="#140a24" />
      <ExecInRow id="execInB"   label="B"     arrived={!!st?.b} showDot borderColor="#140a24" />
      <ExecInRow id="execReset" label="Reset" borderColor="#140a24" />
    </div>
  )
}

// ── Indexed Rendezvous ────────────────────────────────────────────────────────
// The AND-join over an INDEX: B(index) marks an index ready (order-independent);
// A(index) fires execOut once B has marked the SAME index ready, in either order
// (exact match, no arithmetic — increment the A-index upstream for n → n+1).
// Runtime state (window.__RENDEZVOUS_INDEXED_STATE__, keyed by node id) fires
// each index at most once per cycle; wire Reset to onNewResponse so a new cycle's
// indices start fresh. See EventRuntime `rendezvousIndexed` case +
// rendezvousIndexedState.js.

const INT_PIN = '#80c8e8'   // int, matches the PIN_INT convention
const intPinStyle = { background: INT_PIN, width: 10, height: 10, borderRadius: '50%', border: `2px solid ${INT_PIN}55` }

function IntInRow({ id, label }) {
  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 8,
      padding: '5px 12px 5px 16px', borderBottom: '1px solid #140a24', position: 'relative',
    }}>
      <Handle type="target" position={Position.Left} id={id} style={intPinStyle} />
      <span style={{ color: `${INT_PIN}bb`, fontSize: 10 }}>{label}</span>
    </div>
  )
}

export function RendezvousIndexedNode({ id, data, selected }) {
  const fired = isFired(data)
  const st = (typeof window !== 'undefined' && window.__RENDEZVOUS_INDEXED_STATE__?.get(id)) || null
  const pending = st?.pending ?? null
  const ready = st?.ready ?? []

  return (
    <div style={{
      background: selected ? '#1a0e30' : '#0e0620',
      border: `2px solid ${fired ? '#ffcc44' : selected ? RENDEZVOUS_ACCENT : '#3a2858'}`,
      borderRadius: 6, minWidth: 210, fontFamily: 'var(--m-font)', fontSize: 12,
      boxShadow: fired ? '0 0 14px rgba(255,200,40,0.6)' : selected ? `0 0 12px ${RENDEZVOUS_ACCENT}33` : 'none',
    }}>
      <div style={{
        background: HEADER, padding: '6px 12px',
        display: 'flex', alignItems: 'center', gap: 8, position: 'relative',
        borderRadius: '4px 4px 0 0',
      }}>
        <span style={{ fontSize: 13 }}>⇶</span>
        <span style={{ color: RENDEZVOUS_ACCENT, fontWeight: 'bold', fontSize: 11, letterSpacing: 0.5 }}>Indexed Rendezvous</span>
        <Handle type="source" position={Position.Right} id="execOut" style={execStyle} />
      </div>

      <div style={{ padding: '6px 12px', color: '#6a5a80', fontSize: 10, borderBottom: '1px solid #140a24' }}>
        Fires when A and B arrive with the same index.
      </div>

      <ExecInRow id="execInA" label="A (go)" borderColor="#140a24" />
      <IntInRow  id="in-indexA" label="A index" />
      <ExecInRow id="execInB" label="B (ready)" borderColor="#140a24" />
      <IntInRow  id="in-indexB" label="B index" />
      <ExecInRow id="execReset" label="Reset" borderColor="#140a24" />

      <div style={{ padding: '5px 12px', color: '#6a5a80', fontSize: 10 }}>
        pending: <span style={{ color: pending !== null ? '#7ad29a' : '#4a3a60' }}>{pending !== null ? pending : '—'}</span>
        {'  '}ready: <span style={{ color: ready.length ? RENDEZVOUS_ACCENT : '#4a3a60' }}>[{ready.join(',')}]</span>
      </div>
    </div>
  )
}
